import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";    
import ExportPDF from "../../utils/ExportPDF";
import "./NewsListing.css";

import Loading from "../../components/Loading/Loading";
import Message from "../../components/Message/Message";
import logoDefault from "../../assets/images/amtex_logo.png";
import Filter from "../../components/Filter/Filter";
import { MEDIA_TYPES_ALL } from "../../constants/mediaTypes";
import ActionIconButton from "../../components/ActionIconButton/ActionIconButton";
import ActionsButtons from "../../components/ActionsButtons/ActionsButtons";
import ClickableNewsList from "../../components/NewsList/ClickableNewsList";

import { ReactComponent as FilterCategoryIcon } from "../../assets/icons/filter_category_icon.svg";
import { ReactComponent as FilterPeriodIcon } from "../../assets/icons/filter_period_icon.svg";
import { ReactComponent as FilterWordsIcon } from "../../assets/icons/filter_words_icon.svg";
import { ReactComponent as FilterMediaIconIcon } from "../../assets/icons/filter_media_type_icon.svg";
import { ReactComponent as FilterSourceIcon } from "../../assets/icons/filter_source_icon.svg";
import { ReactComponent as SearchIcon } from "../../assets/icons/search_icon.svg";

import { fetchClientClippings } from "../../services/api/fetchClipping";
import { fetchCategories } from "../../services/api/fetchCategories";
import { fetchSourcesByMediaType } from "../../services/api/fetchSources";
import { fetchClientClippingDetail } from "../../services/api/fetchClipping";
import { fetchClientsDetails } from "../../services/api/fetchClients";

function NewsListing() {
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState([]);
  const [mediaType, setMediaType] = useState("");
  const [vehicle, setVehicle] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [data, setData] = useState({
    results: [],
    next: null,
    previous: null,
  });
  const pdfExporterRef = useRef();
  const [brand, setBrand] = useState(null);

  useEffect(() => {
    fetchCategories()
      .then((res) => setCategories(res))
      .catch(() => setCategories([]));
  }, []);

  useEffect(() => {
    const loadClientDetails = async () => {
      try {
        const details = await fetchClientsDetails();
        setBrand(details || null);
      } catch (err) {
        console.error("Erro ao carregar brand:", err);
        setBrand(null);
      }
    };

    loadClientDetails();
  }, []);

  const todayAt00 = () => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  };

  const [dateRange, setDateRange] = useState(() => {
    const end = todayAt00();
    const start = new Date(end);
    start.setDate(end.getDate() - 7);
    return [start, end];
  });

  const [searchTerm, setSearchTerm] = useState("");

  const toISO = (d) =>
    d ? new Date(d).toISOString().slice(0, 10) : undefined;

  const loadClippings = async (params = {}) => {
    try {
      setIsLoading(true);
      setMessage(null);

      const response = await fetchClientClippings(params);
      setData({
        results: response?.results || [],
        next: response?.next || null,
        previous: response?.previous || null,
      });
    } catch (err) {
      console.error("Erro ao carregar notícias:", err);
      setMessage({ type: "error", message: "Erro ao carregar notícias." });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const [start, end] = dateRange || [];
    if (start && end) {
      loadClippings({
        date_start: toISO(start),
        date_end: toISO(end),
      });
    }
  }, []);

  const handleVehicleSearch = async (searchTerm) => {
    if (!mediaType) return [];
    try {
      return await fetchSourcesByMediaType(searchTerm, mediaType);
    } catch (err) {
      console.error("Erro ao buscar veículos:", err);
      return [];
    }
  };

  const handleNext = () => {
    if (data.next) {
      const url = new URL(data.next);
      const cursor = url.searchParams.get("cursor");
      loadClippings({ cursor });
    }
  };

  const handlePrevious = () => {
    if (data.previous) {
      const url = new URL(data.previous);
      const cursor = url.searchParams.get("cursor");
      loadClippings({ cursor });
    }
  };

  const handleToggleSelect = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

const handleExport = async () => {
  if (!selectedIds.length) {
    setMessage({ type: "error", message: "Selecione ao menos uma notícia para exportar." });
    setTimeout(() => setMessage(null), 5000);
    return;
  }

  setIsLoading(true);
  setMessage(null);

  try {
    // busca detalhes de cada clipping
    const newsItems = await Promise.all(
      selectedIds.map((id) => fetchClientClippingDetail(id))
    );

    if (pdfExporterRef.current) {
      await pdfExporterRef.current.exportPDF(
        newsItems,
        "",              // title
        "",              // subtitle
        "dataRecente",   // sortOption
        true,            // includeTitleAndSubtitle
        true,            // includeSummary
        "left",          // logoPosition
        14,              // titleFontSize
        10,              // subtitleFontSize
        "gray",          // titleColor
        true,            // showLogoOnCover
        "resumido",      // summaryType
        false,           // showTermsSummary
        "image"          // printedMode
      );
    }

    setMessage({ type: "success", message: "PDF gerado com sucesso!" });
    setTimeout(() => setMessage(null), 5000);
  } catch (err) {
    console.error("Erro ao exportar PDF:", err);
    setMessage({ type: "error", message: "Erro ao exportar PDF." });
    setTimeout(() => setMessage(null), 5000);
  } finally {
    setIsLoading(false);
  }
};

  return (
    <div className="container-news-listing">
      <div className="news-listing-logo-center">
      <img
        src={brand?.brand  || logoDefault}
        alt={brand?.company_name  ? `Logo ${brand.company_name }` : "Logo"}
        className="news-listing-logo"
      />
      </div>

      <div className="row">
        <Filter
          type="date-range"
          icon={FilterPeriodIcon}
          placeholder="Período de publicação"
          value={dateRange}
          onChange={setDateRange}
        />

        <Filter
          type="text"
          icon={FilterWordsIcon}
          placeholder="Palavra-chave"
          value={searchTerm}
          onChange={setSearchTerm}
        />

        <Filter
          type="multi-select"
          icon={FilterCategoryIcon}
          placeholder="Categoria"
          value={selectedCategory}
          onChange={setSelectedCategory}
          options={categories}
        />

        <Filter
          type="select"
          icon={FilterMediaIconIcon}
          placeholder="Tipo de mídia"
          value={mediaType}
          onChange={setMediaType}
          options={MEDIA_TYPES_ALL}
        />

        <Filter
          type="autocomplete"
          icon={FilterSourceIcon}
          placeholder="Buscar veículo"
          value={vehicle}
          onChange={(option) => setVehicle(option)} 
          onSearch={handleVehicleSearch}
          disabled={!mediaType}
        />

        <ActionIconButton
          icon={SearchIcon}
          tooltip="Buscar notícias"
          tooltipPosition="left"
          onClick={() => {
            const [start, end] = dateRange || [];

            const categoryIds = (selectedCategory || [])
              .map((c) => c.id ?? c.value)
              .filter(Boolean);

            const params = {
              date_start: toISO(start),
              date_end: toISO(end),
              text: searchTerm || undefined,
              source_type: mediaType || undefined,
              sources: vehicle?.value || vehicle?.id || undefined,
            };

            if (categoryIds.length === 1) {
              params.category = categoryIds[0];
            } else if (categoryIds.length > 1) {
              params.categories = categoryIds.join(",");
            }

            loadClippings(params);
          }}
        />
      </div>

      <ActionsButtons
        text="Exportar"
        onClick={handleExport}
        showCancel={false}
        showSecondary={false}
        showTertiary={false}
      />

      <ClickableNewsList
        items={data.results}
        showCursor={true}
        next={data.next}
        previous={data.previous}
        onNext={handleNext}
        onPrevious={handlePrevious}
        selectable={true}
        selectedItems={selectedIds}
        onSelect={handleToggleSelect}
      />

      <ExportPDF ref={pdfExporterRef}/>
      {isLoading && <Loading />}
      {message && <Message type={message.type} message={message.message} />}
    </div>
  );
}

export default NewsListing;
